import { createStore, compose, applyMiddleware, combineReducers } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/logOnlyInProduction';
import thunk from 'redux-thunk';
import { reduxEventReporter } from '../services/statistics/insights/custom/redux/reduxStatisticsEventBuilder';
import { applicationSettings } from '../settings/application';
import { sentryRedux } from './sentryRedux';

export function getDefaultMiddleware(thunkInjection) {
  return [
    thunk.withExtraArgument(thunkInjection),
    store => next => action => {
      reduxEventReporter(action, store.getState(), next);
      return next(action);
    },
  ];
}

/**
 * @param {any} obj The object to inspect.
 * @returns {boolean} True if the argument appears to be a plain object.
 */
function isPlainObject(obj) {
  if (typeof obj !== 'object' || obj === null) return false;

  let proto = obj;
  while (Object.getPrototypeOf(proto) !== null) {
    proto = Object.getPrototypeOf(proto);
  }

  return Object.getPrototypeOf(obj) === proto;
}

export default function create(options = {}) {
  const sentryReduxEnhancer = sentryRedux();

  const {
    reducer,
    thunkInjection,
    middleware = getDefaultMiddleware(thunkInjection),
    devTools = true,
    preloadedState,
    enhancers = [sentryReduxEnhancer],
  } = options;

  let rootReducer;

  if (typeof reducer === 'function') {
    rootReducer = reducer;
  } else if (isPlainObject(reducer)) {
    rootReducer = combineReducers(reducer);
  } else {
    throw new Error(
      'Reducer argument must be a function or an object of functions that can be passed to combineReducers'
    );
  }

  const middlewareEnhancer = applyMiddleware(...middleware);

  const storeEnhancers = [middlewareEnhancer, ...enhancers];

  const finalCompose = devTools ? composeWithDevTools : compose;

  const composedEnhancer = finalCompose(...storeEnhancers);

  const store = createStore(rootReducer, preloadedState, composedEnhancer);

  // Expose the store to enable white-box testing
  if (!applicationSettings.isProd) {
    window.reduxStore = store;
  }

  return store;
}
